import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { ChamferFeatureDefinitionModel } from '../../../definition/chamfer/ChamferFeatureDefinition';
import { ChamferFeaturePrototype } from '../../../prototype/chamfer/ChamferFeaturePrototype';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';
import { ChamferCommitExecution } from './ChamferCommitExecution';
import {
    FeatureCommitExecutionHandler,
    type FeatureCommitExecutionInput,
    type FeatureCommitExecutionResult,
} from '../FeatureCommitExecutionHandler';

/**
 * 倒角特征提交执行处理器。
 */
export class ChamferCommitExecutionHandler extends FeatureCommitExecutionHandler {
    /** 倒角提交执行器。 */
    private readonly execution: ChamferCommitExecution;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;

    /**
     * @param execution 倒角提交执行器。
     * @param runtime 执行运行时。
     */
    public constructor(execution: ChamferCommitExecution, runtime: FeatureExecutionRuntime) {
        super();
        this.execution = execution;
        this.runtime = runtime;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeatureCommitExecutionInput): boolean {
        return input.featureKind === 'chamfer' && input.definition instanceof ChamferFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当提交输入中的定义不是倒角特征定义时。
     */
    public async execute(input: FeatureCommitExecutionInput): Promise<FeatureCommitExecutionResult> {
        if (!(input.definition instanceof ChamferFeatureDefinitionModel)) {
            throw new Error('倒角提交处理器收到的 definition 类型不正确');
        }

        const featureId = input.featureId ?? `feature:${Date.now()}`;
        const prototype = new ChamferFeaturePrototype({
            featureId,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });

        if (input.session) {
            const replaced = await this.execution.commitEdit(
                input.session as FeatureEditSession<ChamferFeatureDefinitionModel, ChamferFeaturePrototype>,
                prototype,
            );
            return {
                replaced,
                featureId,
                prototype,
                meshData: null,
            };
        }

        const objectId = await this.execution.commitCreate(prototype);
        const meshData = await this.runtime.generateMeshData(objectId);
        return {
            replaced: false,
            featureId,
            prototype,
            meshData,
            objectId,
        };
    }
}
